import React, { useState } from 'react';
import { isAfter, isBefore } from 'date-fns';
import { add } from 'date-fns/add';
import { Datepicker, Timepicker } from '@econsult/econsult-ui/dist/components/Datepicker';
import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { getScheduledClosures, saveScheduledClosure } from 'store/action/demandManagement';
import { CustomAnchoredToolTip } from 'components/Table';
import { FieldLabelWithPlacement, FieldSet } from 'components/Form';
import { STRINGS } from 'config';
import { Feature, RenderOn } from '@econsult/econsult-ui/dist/components/FlagControlledFeature';
import _some from 'lodash/some';
import { ClosureTypeSelect } from './ClosureTypeSelect';
import { Form, InputRow, Notification } from './style';

const { FEATURE_FLAGS: { SCHEDULED_CLOSURE_TYPE_ENABLED } } = STRINGS;

const getClosureDateTimesFromForm = formElements => {
  const { startDate, startTime, endDate, endTime } = formElements;
  return [new Date(`${startDate.value}T${startTime.value}`), new Date(`${endDate.value}T${endTime.value}`)];
};

const isOverlapping = (existingClosures, newClosure) => (
  _some(existingClosures, existingClosure => (
    !isBefore(existingClosure.endDateTime, newClosure.startDateTime)
    && !isAfter(existingClosure.startDateTime, newClosure.endDateTime)
  ))
);
const initialStartTimeState = '00:00';
const initialEndTimeState = '00:00';

export const ScheduleClosureForm = () => {
  const dispatch = useAppDispatch();
  const {
    app: {
      selectedPractice,
      selectedPracticeHasAdminRights,
    },
    demandManagement: {
      demandManagementEnabled,
      scheduledClosures,
    },
    featureFlags,
  } = useAppState();

  const [startTime, setStartTime] = useState(initialStartTimeState);
  const [endTime, setEndTime] = useState(initialEndTimeState);
  const [closureType, setClosureType] = useState(null);
  const [validation, setValidation] = useState(null);
  const isScheduledClosureTypeEnabled = featureFlags[SCHEDULED_CLOSURE_TYPE_ENABLED];

  const resetForm = form => {
    form.reset();
    setStartTime(initialStartTimeState);
    setEndTime(initialEndTimeState);
    setClosureType(null);
  };

  const onSaveScheduledClosure = event => {
    event.preventDefault();
    const [startDateTime, endDateTime] = getClosureDateTimesFromForm(event.target.elements);

    if (!isAfter(endDateTime, startDateTime)) {
      return setValidation('Please ensure the scheduled closure start date is before the end date.');
    }
    if (isAfter(endDateTime, add(startDateTime, { days: 7 }))) {
      return setValidation('Scheduled closure must not be longer than 7 days.');
    }
    if (isAfter(new Date(), endDateTime)) {
      return setValidation('The end of the closure must be in the future.');
    }
    const closure = {
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
    };
    if (isScheduledClosureTypeEnabled) {
      if (isOverlapping(scheduledClosures, { startDateTime, endDateTime })) {
        return setValidation(
          'A closure has already been scheduled for this date and time. If you need to change the closure, delete it and schedule a new one.',
        );
      }
      if (!closureType) {
        return setValidation('Please select a closure type.');
      }
      closure.closureType = closureType;
    }
    setValidation(null);
    return saveScheduledClosure(selectedPractice.id, closure)(dispatch)
      .then(isSuccess => {
        if (isSuccess) {
          resetForm(event.target);
          getScheduledClosures(selectedPractice.id)(dispatch);
        }
      });
  };

  return (
    <Form action="" onSubmit={onSaveScheduledClosure}>
      {validation && <Notification state="error" textContent={validation} variant="filled" automationName="scheduledClosuresValidation" />}
      <FieldSet>
        <InputRow itemsCount={3}>
          <FieldLabelWithPlacement row={1} column={1}>
            Start date
            <Datepicker name="startDate" max="9999-12-31" />
          </FieldLabelWithPlacement>

          <FieldLabelWithPlacement row={1} column={2}>
            Start time
            <Timepicker value={startTime} onChange={event => setStartTime(event.target.value)} name="startTime" />
          </FieldLabelWithPlacement>

          <FieldLabelWithPlacement row={2} column={1}>
            End date
            <Datepicker name="endDate" max="9999-12-31" />
          </FieldLabelWithPlacement>

          <FieldLabelWithPlacement row={2} column={2}>
            End time
            <Timepicker value={endTime} onChange={event => setEndTime(event.target.value)} name="endTime" />
          </FieldLabelWithPlacement>

          <Feature flags={[SCHEDULED_CLOSURE_TYPE_ENABLED]}>
            <RenderOn>
              <FieldLabelWithPlacement row={1} column={3} columnSpan={2}>
                Type
                <ClosureTypeSelect closureType={closureType} onSelectClosureType={setClosureType} />
              </FieldLabelWithPlacement>
            </RenderOn>
          </Feature>

          <Button
            disabled={!selectedPracticeHasAdminRights || !demandManagementEnabled}
            type="submit"
            data-automation="SaveScheduledClosureButton"
            size="small"
          >
            Schedule
            {!selectedPracticeHasAdminRights && (
              <CustomAnchoredToolTip
                automationLabel="SaveDemandManagementDisabledTooltip"
                label="Contact your practice admin"
                align="top"
              />
            )}
          </Button>
        </InputRow>
      </FieldSet>
    </Form>
  );
};
